// src/pages/OrdersScreen.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useApp } from '../lib/AppContext';
import { t } from '../lib/i18n';
import { formatEurCheckout } from '../lib/currency';

const S_COLORS = {
  pending:    '#f0a500',
  paid:       '#4cca74',
  processing: '#4a9edd',
  shipped:    '#4a9edd',
  delivered:  '#4cca74',
  cancelled:  '#e05050',
  refunded:   '#888',
};

const STATUS_LABELS = {
  pending:    'Pending Payment',
  paid:       'Paid',
  processing: 'Processing',
  shipped:    'Shipped',
  delivered:  'Delivered',
  cancelled:  'Cancelled',
  refunded:   'Refunded',
};

export default function OrdersScreen() {
  const { lang, user } = useApp();
  const navigate = useNavigate();
  const [orders, setOrders]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    if (!user) return;
    supabase
      .from('orders')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .then(({ data, error }) => {
        if (!error) setOrders(data || []);
        setLoading(false);
      });
  }, [user]);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
      <div className="spinner" />
    </div>
  );

  return (
    <div style={{ maxWidth: 700, margin: '0 auto', padding: '32px 24px' }}>
      <h2 style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: '1.8rem', letterSpacing: '.05em', marginBottom: 20 }}>
        {t('my_orders', lang)}
      </h2>

      {orders.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '4rem 0', color: '#555' }}>
          <div style={{ fontSize: '3rem', marginBottom: 12, opacity: .2 }}>📦</div>
          <p style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '.9rem', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 20 }}>
            No orders yet
          </p>
          <button className="btn-primary" onClick={() => navigate('/')}>
            Start Shopping
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {orders.map(order => (
            <div key={order.id} style={{ background: '#111', borderRadius: 16, border: '1px solid rgba(255,255,255,.07)', overflow: 'hidden' }}>

              {/* Order header — always visible */}
              <button
                onClick={() => setExpanded(expanded === order.id ? null : order.id)}
                style={{ width: '100%', background: 'none', border: 'none', padding: '18px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', gap: 12 }}
                onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,.02)'}
                onMouseOut={e  => e.currentTarget.style.background = 'none'}
              >
                <div style={{ textAlign: 'left', flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '.72rem', color: '#555', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 2 }}>
                    Order #{order.id.slice(0, 8).toUpperCase()}
                  </div>
                  <div style={{ fontSize: '.82rem', color: '#777' }}>
                    {new Date(order.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
                  <span style={{
                    background: `${S_COLORS[order.status] || '#888'}20`,
                    color: S_COLORS[order.status] || '#888',
                    fontFamily: "'Barlow Condensed',sans-serif",
                    fontSize: '.72rem', fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase',
                    padding: '4px 12px', borderRadius: 20,
                  }}>
                    {STATUS_LABELS[order.status] || order.status}
                  </span>

                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: '1.2rem', color: '#4cca74' }}>
                      {formatEurCheckout(order.total_eur)}
                    </div>
                  </div>

                  <span style={{ color: '#444', fontSize: '1rem', transform: expanded === order.id ? 'rotate(90deg)' : 'none', transition: 'transform .2s', flexShrink: 0 }}>›</span>
                </div>
              </button>

              {/* Expanded detail */}
              {expanded === order.id && (
                <div style={{ padding: '0 20px 20px', borderTop: '1px solid rgba(255,255,255,.05)' }}>
                  {/* Items */}
                  <div style={{ marginTop: 16, marginBottom: 14 }}>
                    {(order.items || []).map((item, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '.85rem', marginBottom: 8, gap: 12 }}>
                        <span style={{ color: '#888', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {item.name} × {item.qty}
                        </span>
                        <span style={{ color: '#f4f4f4', flexShrink: 0 }}>
                          {formatEurCheckout(item.price_eur * item.qty)}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Totals breakdown */}
                  <div style={{ borderTop: '1px solid rgba(255,255,255,.05)', paddingTop: 12, marginBottom: 14 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.82rem', marginBottom: 4 }}>
                      <span style={{ color: '#555' }}>Subtotal</span>
                      <span>{formatEurCheckout(order.subtotal_eur)}</span>
                    </div>
                    {order.discount_eur > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.82rem', marginBottom: 4 }}>
                        <span style={{ color: '#4cca74' }}>Discount {order.discount_code ? `(${order.discount_code})` : ''}</span>
                        <span style={{ color: '#4cca74' }}>−{formatEurCheckout(order.discount_eur)}</span>
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.82rem', marginBottom: 4 }}>
                      <span style={{ color: '#555' }}>Shipping</span>
                      <span style={{ color: order.shipping_eur === 0 ? '#4cca74' : '#f4f4f4' }}>
                        {order.shipping_eur === 0 ? 'Free' : formatEurCheckout(order.shipping_eur)}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.9rem', fontWeight: 700, marginTop: 8, paddingTop: 8, borderTop: '1px solid rgba(255,255,255,.05)' }}>
                      <span>Total</span>
                      <span style={{ color: '#4cca74' }}>{formatEurCheckout(order.total_eur)}</span>
                    </div>
                  </div>

                  {/* Shipping address */}
                  {order.shipping_address && (
                    <div style={{ fontSize: '.8rem', color: '#555', marginBottom: 12, lineHeight: 1.8 }}>
                      <div style={{ color: '#777', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '.7rem', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 4 }}>
                        Shipping to
                      </div>
                      {order.shipping_address.full_name}<br />
                      {order.shipping_address.address_line1}<br />
                      {order.shipping_address.city}, {order.shipping_address.postal_code}<br />
                      {order.shipping_address.country_code}
                    </div>
                  )}

                  {/* Loyalty points */}
                  {order.loyalty_points_earned > 0 && (
                    <div style={{ fontSize: '.78rem', color: '#4cca74', marginBottom: 12 }}>
                      +{order.loyalty_points_earned} loyalty points earned
                    </div>
                  )}

                  {/* Tracking (future) */}
                  {order.tracking_number && (
                    <div style={{ background: 'rgba(74,158,221,.08)', border: '1px solid rgba(74,158,221,.2)', borderRadius: 10, padding: '10px 14px', fontSize: '.82rem', color: '#4a9edd', marginBottom: 12 }}>
                      📦 Tracking: <strong>{order.tracking_number}</strong>
                    </div>
                  )}

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <button
                      onClick={() => navigate('/')}
                      style={{ background: 'rgba(76,202,116,.1)', border: '1px solid rgba(76,202,116,.25)', borderRadius: 20, padding: '8px 16px', color: '#4cca74', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '.78rem', fontWeight: 700, cursor: 'pointer' }}
                    >
                      Reorder
                    </button>
                    <button
                      onClick={() => navigate('/contact')}
                      style={{ background: 'none', border: '1px solid rgba(255,255,255,.1)', borderRadius: 20, padding: '8px 16px', color: '#666', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '.78rem', fontWeight: 700, cursor: 'pointer' }}
                    >
                      Contact Support
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}